import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import Table from '../../../components/Table';
import { maskPhoneDisplay } from '../../../utils/validation';
import {
  fetchUnassignedUsers,
  selectUnassignedUsers,
  selectUnassignedTotal,
  selectUnassignedPage,
  selectUnassignedPageSize,
  selectUnassignedLoading,
  setUnassignedPage,
  setUnassignedPageSize,
  assignUsers,
} from './slice';

function AssignUsersModal({ open, onClose, currentType }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const unassignedUsers = useSelector(selectUnassignedUsers);
  const total = useSelector(selectUnassignedTotal);
  const page = useSelector(selectUnassignedPage);
  const pageSize = useSelector(selectUnassignedPageSize);
  const loading = useSelector(selectUnassignedLoading);

  const [selectedIds, setSelectedIds] = useState([]);

  // Fetch unassigned users when modal opens or pagination changes
  useEffect(() => {
    if (open) {
      dispatch(fetchUnassignedUsers({ type: currentType, page, pageSize }));
    }
  }, [dispatch, open, currentType, page, pageSize]);

  // Reset selected IDs when modal closes
  useEffect(() => {
    if (!open) {
      setSelectedIds([]);
    }
  }, [open]);

  const handleAssign = () => {
    if (selectedIds.length > 0) {
      dispatch(assignUsers({ type: currentType, userIds: selectedIds }));
      onClose();
    }
  };

  const handleClose = () => {
    setSelectedIds([]);
    onClose();
  };

  const columns = [
    {
      key: 'username',
      label: t('approvalAccounts.table.username'),
    },
    {
      key: 'email',
      label: t('approvalAccounts.table.email'),
    },
    {
      key: 'phone',
      label: t('approvalAccounts.table.phone'),
      render: (value) => maskPhoneDisplay(value),
    },
    {
      key: 'role',
      label: t('approvalAccounts.table.role'),
    },
    {
      key: 'status',
      label: t('approvalAccounts.table.status'),
      render: (value) => {
        const isActive = value === true || value === 1 || value === '1';
        const hasStatus = value !== undefined && value !== null;

        return (
          <Box
            component="span"
            sx={{
              px: 1.5,
              py: 0.5,
              borderRadius: 1,
              fontSize: '0.875rem',
              fontWeight: 500,
              backgroundColor: hasStatus ? (isActive ? '#d4edda' : '#f8d7da') : '#e2e8f0',
              color: hasStatus ? (isActive ? '#155724' : '#721c24') : '#64748b',
            }}
          >
            {hasStatus
              ? (isActive ? t('approvalAccounts.status.active') : t('approvalAccounts.status.inactive'))
              : '-'
            }
          </Box>
        );
      },
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          color: 'primary.main',
        }}
      >
        <PersonAddIcon />
        {t('approvalAccounts.assignModal.title')}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Table
            id="assign-users-table"
            columns={columns}
            data={unassignedUsers}
            selectable={true}
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            loading={loading}
            emptyMessage={t('approvalAccounts.assignModal.noData')}
            idField="id"
            pagination={true}
            page={page}
            pageSize={pageSize}
            totalItems={total}
            onPageChange={(newPage) => dispatch(setUnassignedPage(newPage))}
            onPageSizeChange={(newSize) => dispatch(setUnassignedPageSize(newSize))}
            pageSizeOptions={[5, 10, 20, 50]}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          startIcon={<CloseIcon />}
          id="cancelAssign"
          name="cancelAssign"
          data-testid="approvalAccounts-assignModal-button-cancelAssign"
        >
          {t('approvalAccounts.assignModal.cancel')}
        </Button>
        <Button
          onClick={handleAssign}
          variant="contained"
          color="primary"
          startIcon={<PersonAddIcon />}
          disabled={selectedIds.length === 0}
          id="confirmAssign"
          name="confirmAssign"
          data-testid="approvalAccounts-assignModal-button-confirmAssign"
        >
          {t('approvalAccounts.assignModal.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AssignUsersModal;
